'use client';

/**
 * Full-height discovery section — an intro column (headline, description,
 * pill CTA) beside a rail of numbered glass cards. One card is open
 * (336px, title + description showing); the rest are 70px strips that open
 * on hover, focus or tap, so the rail reads as an accordion of the
 * platform's learning formats.
 *
 * <pre>
 *   ┌──────────────────────────────────────────────────────────┐
 *   │                          ┌────────────┐┌──┐┌──┐┌──┐┌──┐  │
 *   │   Öğrenmenin             │01          ││02││03││04││05│  │
 *   │   *her halini keşfet.*   │            ││  ││  ││  ││  │  │
 *   │   Description copy       │ Card title ││  ││  ││  ││  │  │
 *   │   ( Tüm eğitimleri → )   │ Card copy  ││  ││  ││  ││  │  │
 *   │                          └────────────┘└──┘└──┘└──┘└──┘  │
 *   └──────────────────────────────────────────────────────────┘
 * </pre>
 *
 * <p>Every card can carry a background image and a link. With a mouse the
 * hovered card opens and a click follows its link; on touch the first tap
 * opens a card and the second follows the link; from the keyboard focus
 * opens it and Enter follows it. The last card opened stays open.
 *
 * <p>Type follows the design: 40px headline (asterisk-wrapped words in
 * italic), 18px Regular #B1B1B1 description, 13px SemiBold CTA — in the
 * theme's font. The background colour and the glow are authorable.
 * Full height comes from {@link FullHeightSlider} (as a single slide), and
 * the glow is its backdrop.
 */
import { useMemo, useRef, useState, type MouseEvent } from 'react';
import { Button } from '@akademi/ui';
import {
  PortalLink,
  isVisible,
  useBlockPart,
  useCurrentUser,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { asList, tx } from '../_shared/coerce';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { withEmphasis } from '../_shared/emphasis';
import { FullHeightFrame, FullHeightSlider } from '../_shared/FullHeightSlider';

type Localized = string | Record<string, string>;

export interface FullHeightDiscoveryCard extends CampaignAudience {
  title?: Localized;
  description?: Localized;
  /** Background image, cropped to the card at every width. */
  image?: string;
  /** Leave empty when the image is decorative. */
  imageAlt?: string;
  href?: string;
  newTab?: boolean;
}

export interface FullHeightDiscoveryProps {
  /** `*asterisks*` around a phrase set it in italic. */
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  ctaNewTab?: boolean;
  cards?: FullHeightDiscoveryCard[];
  /** 1-based card that starts open. Out of range falls back to the first. */
  initialCard?: number;
  backgroundColor?: string;
  showGlow?: boolean;
  glowColor?: string;
  titleTag?: 'h1' | 'h2';
}

// Card motion, hover and every breakpoint live here — inline styles can't
// express them. Layout: intro + rail side by side from 1240px (the width a
// 400px column, a 756px rail and a gutter need), stacked below that, and
// the rail turns into a vertical accordion under 640px where six 70px
// columns can't fit. The vh line is the fallback for engines without dvh.
const FULL_HEIGHT_DISCOVERY_CSS = `
.fh-slider-frame.fhd-frame { padding-inline: 24px; }
.fhd-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  width: 100%;
  max-width: 1376px;
  margin: 0 auto;
}
/* The design sits the intro 7px below the rail's centre line. */
.fhd-copy { flex: 0 1 400px; min-width: 0; padding-top: 14px; }
.fhd-desc { margin: 54px 0 0; }
.fhd-copy .fhd-cta { margin-top: 60px; }
.fhd-cta { background-color: rgba(255, 255, 255, 0.28); color: #ffffff; }
.fhd-cta:hover { background-color: rgba(255, 255, 255, 0.36); }
.fhd-rail {
  display: flex;
  gap: 14px;
  height: clamp(420px, 60.3vh, 656px);
  height: clamp(420px, 60.3dvh, 656px);
  max-width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}
.fhd-card {
  position: relative;
  flex: 0 0 70px;
  min-width: 0;
  transition: flex-basis 500ms cubic-bezier(0.4, 0, 0.2, 1);
}
/* Fixed, not shrinkable: the card's content is absolutely positioned, so a
   shrinkable open card has no min-content and would collapse to nothing in
   a content-sized rail. The stacked layout below gives the rail a definite
   width, and only there may the open card give way. */
.fhd-card[data-open] { flex: 0 0 336px; }
.fhd-card-link {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.155);
  color: #ffffff;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 200ms ease;
}
.fhd-card-link:hover { border-color: rgba(255, 255, 255, 0.32); }
.fhd-card-link:focus-visible { outline: 2px solid rgba(255, 255, 255, 0.85); outline-offset: 2px; }
.fhd-num {
  position: absolute;
  top: 20px;
  left: 12px;
  transition: left 500ms cubic-bezier(0.4, 0, 0.2, 1);
}
.fhd-card[data-open] .fhd-num { left: 28px; }
.fhd-body {
  position: absolute;
  left: 28px;
  right: 28px;
  bottom: 55px;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 150ms ease, transform 150ms ease;
}
.fhd-card[data-open] .fhd-body {
  opacity: 1;
  transform: none;
  transition: opacity 300ms ease 220ms, transform 300ms ease 220ms;
}
@media (max-width: 1239px) {
  .fhd-layout { flex-direction: column; align-items: flex-start; }
  .fhd-copy { flex: none; max-width: 560px; padding-top: 0; }
  .fhd-rail {
    width: 100%;
    height: clamp(360px, 48vh, 520px);
    height: clamp(360px, 48dvh, 520px);
  }
  .fhd-card[data-open] { flex: 0 1 336px; }
}
@media (max-width: 639px) {
  .fh-slider-frame.fhd-frame { padding-inline: 16px; }
  .fhd-layout { gap: 40px; }
  .fhd-desc { margin-top: 28px; }
  .fhd-copy .fhd-cta { margin-top: 32px; }
  .fhd-rail { flex-direction: column; width: 100%; height: auto; }
  .fhd-card { flex: 0 0 70px; transition-property: flex-basis; }
  .fhd-card[data-open] { flex: 0 0 336px; }
  .fhd-num, .fhd-card[data-open] .fhd-num { left: 20px; }
  .fhd-body { left: 20px; right: 20px; bottom: 28px; }
}
@media (prefers-reduced-motion: reduce) {
  .fhd-card, .fhd-num, .fhd-body, .fhd-card[data-open] .fhd-body { transition: none; }
}
`;

/** Sampled from the design: a strong light low on the left and a fainter
 *  one along the top edge, both in the glow colour, over the background. */
const glowGradient = (color: string) => {
  const mix = (strength: number) => `color-mix(in srgb, ${color} ${strength}%, transparent)`;
  return (
    `radial-gradient(60% 116% at 12% 57%, ${color} 0%, ${mix(72)} 27%, ${mix(43)} 48%, ` +
    `${mix(33)} 57%, ${mix(12)} 80%, transparent 100%), ` +
    `radial-gradient(55% 27% at 47% 0%, ${mix(70)} 0%, ${mix(56)} 40%, ${mix(34)} 62%, ` +
    `${mix(13)} 83%, transparent 100%)`
  );
};

const cardsOf = (cards: unknown) =>
  asList<FullHeightDiscoveryCard>(cards).filter(
    (c) => c && (tx(c.title) || tx(c.description) || c.image),
  );

export function FullHeightDiscovery({
  title,
  description,
  ctaLabel,
  ctaHref,
  ctaNewTab = false,
  cards: rawCards,
  initialCard = 1,
  backgroundColor = '#0a0a0a',
  showGlow = true,
  glowColor = '#1b253d',
  titleTag = 'h2',
}: FullHeightDiscoveryProps) {
  const currentUser = useCurrentUser<CurrentUser>();
  const part = useBlockPart();

  const cards = useMemo(
    () => cardsOf(rawCards).filter((c) => isVisible(c, currentUser)),
    [rawCards, currentUser],
  );

  const titleText = tx(title);
  const descriptionText = tx(description);
  const ctaText = tx(ctaLabel);
  const hasContent = Boolean(titleText || descriptionText || ctaText || cards.length > 0);
  const TitleTag = titleTag === 'h1' ? 'h1' : 'h2';

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: FULL_HEIGHT_DISCOVERY_CSS }} />
      <FullHeightSlider
        slides={hasContent ? [0] : []}
        background={backgroundColor}
        backdrop={
          showGlow ? (
            <div style={{ position: 'absolute', inset: 0, background: glowGradient(glowColor) }} />
          ) : null
        }
        renderSlide={(_, __, { hasControls }) => (
          <FullHeightFrame
            hasControls={hasControls}
            className="fhd-frame"
            style={{ alignItems: 'center' }}
          >
            <div className="fhd-layout" style={{ fontFamily: 'var(--font-family-body, inherit)' }}>
              <div className="fhd-copy">
                {titleText ? (
                  <TitleTag
                    {...part('title')}
                    style={{
                      margin: 0,
                      fontFamily: 'var(--font-family-display, inherit)',
                      fontSize: 40,
                      fontWeight: 500,
                      lineHeight: 1.21,
                      color: '#ffffff',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {withEmphasis(titleText, 700)}
                  </TitleTag>
                ) : null}
                {descriptionText ? (
                  <p
                    {...part('subtitle')}
                    className="fhd-desc"
                    style={{
                      ...(titleText ? null : { marginTop: 0 }),
                      fontSize: 18,
                      fontWeight: 400,
                      lineHeight: '21px',
                      color: '#B1B1B1',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {descriptionText}
                  </p>
                ) : null}
                {ctaText ? (
                  <Button
                    {...part('cta')}
                    href={ctaHref || '#'}
                    radius="pill"
                    className="fhd-cta"
                    {...(ctaNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    style={{
                      // Layout doesn't lean on the Button's utility classes.
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 14,
                      boxSizing: 'border-box',
                      height: 46,
                      ...(titleText || descriptionText ? null : { marginTop: 0 }),
                      padding: '0 23px 0 24px',
                      border: '1px solid rgba(127, 146, 227, 0.5)',
                      fontFamily: 'inherit',
                      fontSize: 13,
                      fontWeight: 600,
                      textDecoration: 'none',
                    }}
                  >
                    {ctaText}
                    <svg
                      aria-hidden="true"
                      width="16"
                      height="12"
                      viewBox="0 0 16 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.6}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 6h14M10 1l5 5-5 5" />
                    </svg>
                  </Button>
                ) : null}
              </div>
              {cards.length > 0 ? <DiscoveryRail cards={cards} initialCard={initialCard} /> : null}
            </div>
          </FullHeightFrame>
        )}
      />
    </>
  );
}

// =============================================================================
// The card accordion
// =============================================================================

function DiscoveryRail({
  cards,
  initialCard,
}: {
  cards: FullHeightDiscoveryCard[];
  initialCard: number;
}) {
  const part = useBlockPart();
  const start = Number(initialCard) - 1;
  const [rawOpen, setOpen] = useState(Number.isInteger(start) && start >= 0 ? start : 0);
  // The card list can shrink under us (an expiry, an admin edit).
  const open = rawOpen < cards.length ? rawOpen : 0;
  // A finger's first tap must open a card, not also follow its link. Hover
  // only opens for a mouse or pen, and the focus a tap brings is ignored, so
  // the click that follows still sees the card closed and just opens it.
  const touchPress = useRef(false);

  return (
    <ul {...part('list')} className="fhd-rail">
      {cards.map((card, i) => {
        const isOpen = i === open;
        const cardTitle = tx(card.title);
        const cardDescription = tx(card.description);
        const alt = card.imageAlt?.trim() ?? '';

        const onClick = (e: MouseEvent) => {
          touchPress.current = false;
          if (isOpen) return;
          // Closed card: this press opens it; the next one follows the link.
          e.preventDefault();
          setOpen(i);
        };

        const inner = (
          <>
            {card.image ? (
              <>
                <img
                  src={resizeImageUrl(card.image, '800x0')}
                  alt={alt}
                  aria-hidden={alt ? undefined : true}
                  loading="lazy"
                  decoding="async"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {/* Keeps the number and copy legible over any photo. */}
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.1) 35%, rgba(0,0,0,0.7) 100%)',
                  }}
                />
              </>
            ) : null}
            <span
              className="fhd-num"
              style={{
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.21,
                color: 'rgba(255, 255, 255, 0.6)',
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="fhd-body" style={{ display: 'block' }}>
              {cardTitle ? (
                <span
                  style={{
                    display: 'block',
                    fontSize: 18,
                    fontWeight: 600,
                    lineHeight: 1.21,
                    color: '#ffffff',
                  }}
                >
                  {cardTitle}
                </span>
              ) : null}
              {cardDescription ? (
                <span
                  style={{
                    display: 'block',
                    marginTop: cardTitle ? 23 : 0,
                    fontSize: 15,
                    fontWeight: 400,
                    lineHeight: '19px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {cardDescription}
                </span>
              ) : null}
            </span>
          </>
        );

        const shared = {
          className: 'fhd-card-link',
          onPointerDown: (e: { pointerType: string }) => {
            touchPress.current = e.pointerType === 'touch';
          },
          onPointerEnter: (e: { pointerType: string }) => {
            if (e.pointerType !== 'touch') setOpen(i);
          },
          onFocus: () => {
            if (!touchPress.current) setOpen(i);
          },
        };

        return (
          <li key={i} {...part('item', i)} className="fhd-card" data-open={isOpen ? '' : undefined}>
            {card.href ? (
              <PortalLink
                href={card.href}
                {...shared}
                onClick={onClick}
                {...(card.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {inner}
              </PortalLink>
            ) : (
              <button
                type="button"
                {...shared}
                aria-expanded={isOpen}
                onClick={() => {
                  touchPress.current = false;
                  setOpen(i);
                }}
              >
                {inner}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
